import { type ColDef } from "ag-grid-community";
import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { SentIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { TextSkeleton } from "shared/components/skeletons/text-skeleton";
import type { IdentityTheftProtectionRow } from "./types";

const STATUS_BADGE_VARIANT: Record<string, BadgeVariant> = {
  Enrolled: BadgeVariant.SUCCESS,
  Invited: BadgeVariant.PRIMARY,
  Declined: BadgeVariant.ERROR,
  Unenrolled: BadgeVariant.BASE
};

export const getColumns = (
  onResendInvite: (row: IdentityTheftProtectionRow) => void
): ColDef<IdentityTheftProtectionRow>[] => [
  {
    field: "name",
    headerName: "Name",
    minWidth: 140,
    width: 220,
    pinned: "left",
    cellRenderer: (params: { value: string }) => (
      <span className="text-sm font-medium text-content-1000 truncate">
        {params.value}
      </span>
    )
  },
  {
    field: "property",
    headerName: "Property",
    flex: 1,
    width: 250,
    minWidth: 150,
    cellRenderer: (params: { data: IdentityTheftProtectionRow }) => (
      <div className="min-w-0 flex flex-col gap-0.5 justify-center">
        <p className="text-content-1000 text-sm truncate">
          {params.data.property}
        </p>
        <p className="text-content-400 text-xs font-normal truncate">
          {params.data.address}
        </p>
      </div>
    )
  },
  {
    field: "email",
    headerName: "Email",
    flex: 1,
    width: 220,
    minWidth: 140,
    cellRenderer: (params: { value: string }) => (
      <span className="text-sm font-medium text-content-500 truncate">
        {params.value}
      </span>
    )
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    minWidth: 90,
    cellRenderer: (params: { value: string }) => {
      const variant = Object.prototype.hasOwnProperty.call(
        STATUS_BADGE_VARIANT,
        params.value
      )
        ? STATUS_BADGE_VARIANT[params.value]
        : BadgeVariant.BASE;
      return (
        <Badge variant={variant} size={BadgeSize.SM}>
          {params.value}
        </Badge>
      );
    }
  },
  {
    field: "lastActivity",
    headerName: "Last Activity",
    flex: 1,
    width: 220,
    minWidth: 150,
    cellRenderer: (params: { data: IdentityTheftProtectionRow }) => (
      <div className="min-w-0 flex flex-col gap-0.5 justify-center">
        <p className="text-sm text-content-500 truncate">
          {params.data.lastAction}
        </p>
        <p className="text-xs text-content-400 truncate">
          {params.data.lastActivity} at {params.data.lastActivityTime}
        </p>
      </div>
    )
  },
  {
    colId: "actions",
    headerName: "",
    width: 80,
    suppressSizeToFit: true,
    cellRenderer: (params: { data: IdentityTheftProtectionRow }) => {
      const isInvited = params.data.status === "Invited";
      return (
        <ContextMenuPopover
          placement="bottom-start"
          actions={[
            {
              title: (
                <span className="flex gap-2 items-center">
                  <HugeiconsIcon icon={SentIcon} size={16} />
                  Resend invite
                </span>
              ),
              disabled: !isInvited,
              tooltip: !isInvited
                ? "Can only resend invites for pending enrollments"
                : undefined,
              onClick: (e) => {
                e.stopPropagation();
                onResendInvite(params.data);
              }
            }
          ]}
        />
      );
    }
  }
];

export const SKELETON_ROW_COUNT = 10;

export const skeletonColumns: ColDef<{ id: number }>[] = [
  {
    field: "id",
    headerName: "Name",
    minWidth: 140,
    width: 220,
    pinned: "left",
    cellRenderer: () => <TextSkeleton size="sm" className="w-3/4" />
  },
  {
    colId: "property",
    headerName: "Property",
    flex: 1,
    width: 250,
    minWidth: 150,
    cellRenderer: () => (
      <div className="flex flex-col gap-1.5 w-full">
        <TextSkeleton size="xs" className="w-4/5" />
        <TextSkeleton size="xxs" className="w-3/5" />
      </div>
    )
  },
  {
    colId: "email",
    headerName: "Email",
    flex: 1,
    width: 220,
    minWidth: 140,
    cellRenderer: () => <TextSkeleton size="sm" className="w-4/5" />
  },
  {
    colId: "status",
    headerName: "Status",
    width: 120,
    minWidth: 90,
    cellRenderer: () => <TextSkeleton size="sm" className="w-16 rounded-2xl" />
  },
  {
    colId: "lastActivity",
    headerName: "Last Activity",
    flex: 1,
    width: 220,
    minWidth: 150,
    cellRenderer: () => (
      <div className="flex flex-col gap-1.5 w-full">
        <TextSkeleton size="xs" className="w-3/5" />
        <TextSkeleton size="xxs" className="w-2/5" />
      </div>
    )
  },
  {
    colId: "actions",
    headerName: "",
    width: 80,
    cellRenderer: () => null
  }
];

export const skeletonRows = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, i) => ({ id: i })
);
